class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    
    // Subscribe to cart updates
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      // Update checkout button text after cart update with longer delay to ensure DOM is ready
      setTimeout(() => {
        this.updateCheckoutButtonText();
        // Also restart the observer in case the DOM was rebuilt
        this.setupOnwardFlexObserver();
      }, 100);
    });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
        
        // Update checkout button text when drawer opens with multiple attempts
        this.updateCheckoutButtonText();
        setTimeout(() => this.updateCheckoutButtonText(), 50);
        setTimeout(() => this.updateCheckoutButtonText(), 150);
        setTimeout(() => this.updateCheckoutButtonText(), 300);
        setTimeout(() => this.updateCheckoutButtonText(), 600);
        // Set up observer to watch for onward-flex visibility changes
        this.setupOnwardFlexObserver();
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    
    // Clean up observers
    if (this.onwardObserver) {
      this.onwardObserver.disconnect();
      this.onwardObserver = null;
    }
    if (this.onwardMutationObserver) {
      this.onwardMutationObserver.disconnect();
      this.onwardMutationObserver = null;
    }
  }

  disconnectedCallback() {
    // Clean up pub/sub subscription when element is removed
    if (this.cartUpdateUnsubscriber) {
      this.cartUpdateUnsubscriber();
    }
    // Reset retry count
    this.onwardFlexRetryCount = 0;
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // Delay the button text update to ensure DOM is fully rendered
      setTimeout(() => {
        this.updateCheckoutButtonText();
        this.setupOnwardFlexObserver();
      }, 100);
      this.open();
    });
  }

  updateCheckoutButtonText() {
    const checkoutButton = document.querySelector('#CartDrawer-Checkout');
    const onwardFlex = document.querySelector('.onward-flex');
    
    if (!checkoutButton) {
      console.log('Checkout button not found');
      return;
    }
    
    if (onwardFlex) {
      // Check if onward-flex is visible
      const isVisible = onwardFlex.offsetWidth > 0 && onwardFlex.offsetHeight > 0 && 
                       window.getComputedStyle(onwardFlex).display !== 'none' &&
                       window.getComputedStyle(onwardFlex).visibility !== 'hidden';
      
      console.log('Onward flex found, visible:', isVisible, 'current text:', checkoutButton.textContent);
      
      if (isVisible && checkoutButton.textContent.trim() !== 'Checkout') {
        console.log('Updating button text to Checkout');
        checkoutButton.textContent = 'Checkout';
      }
    } else {
      console.log('Onward flex not found');
    }
  }

  setupOnwardFlexObserver() {
    // Clean up existing observers first to prevent multiple observers
    if (this.onwardObserver) {
      this.onwardObserver.disconnect();
      this.onwardObserver = null;
    }
    if (this.onwardMutationObserver) {
      this.onwardMutationObserver.disconnect();
      this.onwardMutationObserver = null;
    }

    const onwardFlex = document.querySelector('.onward-flex');
    if (!onwardFlex) {
      // Don't keep retrying indefinitely - limit to a few attempts
      if (!this.onwardFlexRetryCount) this.onwardFlexRetryCount = 0;
      if (this.onwardFlexRetryCount < 5) {
        this.onwardFlexRetryCount++;
        setTimeout(() => this.setupOnwardFlexObserver(), 500);
      }
      return;
    }

    // Reset retry count on success
    this.onwardFlexRetryCount = 0;

    // Throttle function to prevent excessive calls
    let throttleTimeout;
    const throttledUpdate = () => {
      if (throttleTimeout) return;
      throttleTimeout = setTimeout(() => {
        this.updateCheckoutButtonText();
        throttleTimeout = null;
      }, 100);
    };

    // Create intersection observer to watch for visibility changes
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        throttledUpdate();
      });
    });

    // Create mutation observer to watch for specific changes to onward-flex only
    const mutationObserver = new MutationObserver((mutations) => {
      let shouldUpdate = false;
      mutations.forEach(mutation => {
        // Only update if the mutation affects the onward-flex element or its visibility
        if (mutation.target === onwardFlex || 
            (mutation.type === 'attributes' && 
             ['style', 'class'].includes(mutation.attributeName))) {
          shouldUpdate = true;
        }
      });
      if (shouldUpdate) {
        throttledUpdate();
      }
    });

    observer.observe(onwardFlex);
    mutationObserver.observe(onwardFlex, {
      attributes: true,
      attributeFilter: ['style', 'class']
    });

    // Store observers for cleanup
    this.onwardObserver = observer;
    this.onwardMutationObserver = mutationObserver;
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
